import useScrapedAnswersByResponseItem from '@app/src/hooks/useScrapedAnswersByResponseItem'
import { AiAnswerBlock } from '@app/src/pages/Questionnaire/Accessor/Report/AiScreening/AiAnswerBlock'
import { CitationsForScrapedAnswer } from '@app/src/pages/Questionnaire/Accessor/Report/CitationsForScrapedAnswer'
import { palette } from '@app/src/theme/palette'
import { QuestionType } from '@app/src/types/reporting'
import type { ResponseItem } from '@app/src/types/resourceExplorer'
import { AnswerStatus } from '@app/src/types/resourceExplorer'
import { ArrowForward, AutoAwesomeOutlined, DeleteOutlined } from '@mui/icons-material'
import { Box, Chip, CircularProgress, Grid, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import React from 'react'
import { useIntl } from 'react-intl'
import FileAnswerContent from '../../FileAnswerContent'
import { getAnswerBasedOnQuestionType } from '../../Table/Cells/AnswerWithFlagCell'
import { useDrawer } from '../DrawerContext'
import type { DrawerViewProps } from '../DrawerView'
import DrawerView from '../DrawerView'
import AiSkippedQuestionInfo from './AiSkippedQuestionInfo'
import DrawerViewDiscardAnswer from './DrawerViewDiscardAnswer'

type DrawerViewAiScreenedAnswerDetailProps = {
  item: ResponseItem
} & Omit<DrawerViewProps, 'title' | 'subtitle'>

const DrawerViewAiScreenedAnswerDetail: React.FC<DrawerViewAiScreenedAnswerDetailProps> = ({ item, ...props }) => {
  const { formatMessage } = useIntl()
  const { closeDrawer, openDrawer } = useDrawer()
  const questionType = item.requestItem.questionTypeId
  const answer = getAnswerBasedOnQuestionType(questionType, item)?.toString() ?? ''

  const { scrapedAnswer, isLoadingScrapedAnswers } = useScrapedAnswersByResponseItem({
    responseItemId: item.id,
  })

  const handleDiscardAnswer = () => {
    openDrawer(<DrawerViewDiscardAnswer responseItemId={item.id} responseId={item.response?.id ?? 0} />)
  }

  if (isLoadingScrapedAnswers) {
    return (
      <Box display="flex" flexGrow={1} justifyContent="center" alignItems="center" flexDirection="column">
        <CircularProgress />
      </Box>
    )
  }

  const renderCitations = () => {
    if (item?.isAnswered) {
      return (
        <CitationsForScrapedAnswer
          scrapedAnswer={scrapedAnswer}
          provider={item.response?.request?.subscriptions?.[0]?.target}
          questionType={item.requestItem?.questionType?.name ?? QuestionType.Text}
        />
      )
    }

    if (answer?.trim()) {
      return null
    }
    return (
      <Box px={2.5} pb={2}>
        <AiSkippedQuestionInfo />
      </Box>
    )
  }

  return (
    <DrawerView
      title={formatMessage({ id: 'schemas.responseItem.answerDetail' })}
      subTitle={item.response?.request?.targetAliasObject?.name}
      buttons={[
        {
          label: formatMessage({ id: 'general.close' }),
          onClick: closeDrawer,
        },
      ]}
      {...props}
    >
      <Stack sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Typography variant="subtitle1" sx={{ px: 3 }}>
          {item.requestItem.description}
        </Typography>

        <Box my={2} mx={3} border={1} borderColor="grey.300" borderRadius={1.5} overflow="hidden">
          <AiAnswerBlock
            actions={
              item.answerStatus === AnswerStatus.Answered && (
                <Tooltip title={formatMessage({ id: 'reporting.discardAiAnswer.discard' })} arrow>
                  <IconButton
                    size="small"
                    onClick={handleDiscardAnswer}
                    sx={{ color: palette.semantic.error }}
                    data-testid="discard-ai-answer"
                  >
                    <DeleteOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              )
            }
          >
            {answer?.trim() ? (
              <Typography>{answer}</Typography>
            ) : item.answerStatus === AnswerStatus.Answered ? (
              <FileAnswerContent item={item} showExpiresAtText />
            ) : (
              <Grid item container spacing={1} alignItems="flex-end">
                <Grid item>
                  <Chip label={formatMessage({ id: 'reporting.skipQuestion.skippedThisQuestion' })} />
                </Grid>
                <Grid item>
                  <Box display="flex" mb={0.5}>
                    <ArrowForward color="inherit" />
                  </Box>
                </Grid>
                <Grid item>
                  <Chip label={formatMessage({ id: `reporting.answerStatus.${item.answerStatus}` })} />
                </Grid>
              </Grid>
            )}
          </AiAnswerBlock>
          {renderCitations()}
        </Box>
      </Stack>

      <Stack sx={{ mt: 'auto', mb: 1, mx: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <AutoAwesomeOutlined />
          <Typography variant="subtitle2" color="text.primary">
            {formatMessage({ id: 'reporting.AiSkippedQuestionInfo.title' })}
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.primary">
          {formatMessage({ id: 'reporting.AiSkippedQuestionInfo.description' })}
        </Typography>
      </Stack>
    </DrawerView>
  )
}

export default DrawerViewAiScreenedAnswerDetail
